import { Box } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import type { MouseEvent } from 'react'
import { useEffect, useRef } from 'react'

import BarSegment from '@/features/bidding/components/cascading/bid-ladder/shared/BarSegment'
import { getBarColors } from '@/features/bidding/components/cascading/bid-ladder/shared/getBarColors'
import VolumeCapacityInput from '@/features/bidding/components/cascading/bid-ladder/volume-allocation/VolumeCapacityInput'
import type { VolumeCapacityConfig } from '@/features/bidding/types/bidLadderInput'
import type { BidLevelActivationGroupSymmetrical } from '@/features/bidding/types/bidLadderInputSymmetrical'

type VolumeBidLadderStackedBarSymmetricalProps = {
  groups: BidLevelActivationGroupSymmetrical[]
  levelId: string
  /** Total MW across ALL levels (for relative bar width comparison) */
  totalMWAcrossAll: number
  /** Currently hovered regulation object code (for cross-highlighting) */
  hoveredRegulationObjectCode?: string | null
  onContextMenu: (event: MouseEvent, group: BidLevelActivationGroupSymmetrical) => void
  onGroupHover?: (regulationObjectCode: string | null) => void
  /** Key of group that should receive focus (format: levelId-regulationObjectCode) */
  focusedGroupKey?: string | null
  /** Callback to clear focused group key after focus is set */
  onClearFocusedGroupKey?: () => void
  /** Volume capacity configuration */
  volumeCapacityConfig: VolumeCapacityConfig
  /** Callback when volume capacity configuration changes */
  onVolumeCapacityChange: (config: VolumeCapacityConfig) => void
  /** Number of decimal places allowed when mode is 'fixed' */
  fixedModeDecimalPlaces?: number
}

function VolumeBidLadderStackedBarSymmetrical({
  groups,
  levelId,
  totalMWAcrossAll,
  hoveredRegulationObjectCode,
  onContextMenu,
  onGroupHover,
  focusedGroupKey,
  onClearFocusedGroupKey,
  volumeCapacityConfig,
  onVolumeCapacityChange,
  fixedModeDecimalPlaces,
}: Readonly<VolumeBidLadderStackedBarSymmetricalProps>) {
  const theme = useTheme()
  const colors = getBarColors(theme)

  // Refs for focus management
  const groupRefs = useRef<Map<string, HTMLDivElement>>(new Map())

  // Focus the group when focusedGroupKey matches
  useEffect(() => {
    if (!focusedGroupKey) return

    const ref = groupRefs.current.get(focusedGroupKey)
    if (ref) {
      ref.focus()
      ref.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
      onClearFocusedGroupKey?.()
    }
  }, [focusedGroupKey, onClearFocusedGroupKey])

  // Calculate MW totals
  const currentMW = groups.reduce((sum, g) => sum + g.capacityMW, 0)
  const levelWidthPercent = totalMWAcrossAll > 0 ? (currentMW / totalMWAcrossAll) * 100 : 0

  // Empty state - show volume capacity input
  if (groups.length === 0) {
    return (
      <Box
        sx={{
          flex: 1,
          height: 32,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <VolumeCapacityInput
          config={volumeCapacityConfig}
          fixedModeDecimalPlaces={fixedModeDecimalPlaces}
          onChange={onVolumeCapacityChange}
        />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        height: 32,
        flex: 1,
        gap: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: `${levelWidthPercent}%`,
          height: '100%',
          gap: '2px',
        }}
      >
        {groups.map((group) => {
          const segmentWidthPercent = currentMW > 0 ? (group.capacityMW / currentMW) * 100 : 0
          const isHighlighted = hoveredRegulationObjectCode === group.regulationObjectCode
          const groupKey = `${levelId}-${group.regulationObjectCode}`

          return (
            <BarSegment
              key={group.uuid}
              capacityMW={group.capacityMW}
              code={group.code}
              colors={colors}
              groupKey={groupKey}
              isHighlighted={isHighlighted}
              refCallback={(el: HTMLDivElement | null) => {
                if (el) {
                  groupRefs.current.set(groupKey, el)
                } else {
                  groupRefs.current.delete(groupKey)
                }
              }}
              regulationObjectCode={group.regulationObjectCode}
              segmentWidthPercent={segmentWidthPercent}
              uuid={group.uuid}
              onContextMenu={(e) => onContextMenu(e, group)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ' || e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
                  e.preventDefault()
                  const rect = e.currentTarget.getBoundingClientRect()
                  const syntheticEvent = {
                    preventDefault: () => {},
                    clientX: rect.left + rect.width / 2,
                    clientY: rect.top + rect.height / 2,
                  } as MouseEvent
                  onContextMenu(syntheticEvent, group)
                }
              }}
              onMouseEnter={() => onGroupHover?.(group.regulationObjectCode)}
              onMouseLeave={() => onGroupHover?.(null)}
            />
          )
        })}
      </Box>
    </Box>
  )
}

export default VolumeBidLadderStackedBarSymmetrical
